/* @flow */
// typecheck: ignore file
import cx from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Coin from 'components/Coin.jsx';
import Group from 'entities/Group';
import Groups from 'collections/Groups';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import GroupName from 'groups/components/GroupName.jsx';
import GroupStore from 'groups/stores/GroupStore';
import Glyph from 'components/Glyph.jsx';
import { mountModal } from 'lib/modal.js';
import UnknownRolesModalContainer from 'components/UnknownRolesModalContainer.jsx';
import Text from 'components/Text.jsx';
import { roleName, EVERYONE, UNKNOWN } from 'utils/roleHelpers';
import {
  deselectGroup,
  changeResultSelection,
  loadGroups,
} from 'reducers/roleBasedMessagingSearch';

import type { GroupRecipient, Role } from 'reducers/roleBasedMessagingSearch';

import './styles/RoleBasedMessagingGroupResultItem.scss';

class RolesToken extends React.Component {
  props: {
    role: Role,
    selected: bool,
    onSelect: (e: Event) => void,
    highlighted: bool,
    isUnknown: bool,
    onMouseEnter: () => void,
  };

  render() {
    const { role, selected, highlighted, isUnknown } = this.props;
    return (
      <div
        onClick={this.props.onSelect}
        onMouseEnter={this.props.onMouseEnter}
        className={cx('RolesToken', {
          'RolesToken-selected': selected,
          'RolesToken-highlighted': highlighted,
          'RolesToken-unknown': isUnknown,
        })}>
        {roleName[role.role || EVERYONE]}
        <div className="RolesToken-count">
          <span className="RolesToken-count-divider" >|</span>
          &nbsp;&nbsp;&nbsp;{role.count}
        </div>
      </div>
    );
  }
}

type Props = {
  recipients: GroupRecipient[],
  highlighted: bool,
  selectedRecipients: GroupRecipient[],
  query: string,
  onSelect: (recipients: GroupRecipient[]) => void,
  onDeselectGroup: (group: Group) => void,
  onMouseEnter: () => void,
  onMouseLeave: () => void,
  resultSelectionIndex: number,
  onChangeResultSelection: (selectedIndex: number) => void,
  onLoadGroups: (groups: Groups) => void,
};

class RoleBasedMessagingGroupResultItem extends React.Component {
  props: Props;

  state: {
    open: bool,
  };

  state = {
    open: false,
  };

  componentWillReceiveProps(nextProps: Props) {
    if (!this.props.selectedRecipients.length && nextProps.selectedRecipients.length) {
      this.setState({ open: true });
    }

    if (this.props.selectedRecipients.length && !nextProps.selectedRecipients.length) {
      this.setState({ open: false });
    }
  }

  handleSelectPrimary() {
    const { recipients } = this.props;
    // With Teacher to Teacher announcements there may not be a group
    const recipientWithGroup = recipients.find((r: GroupRecipient) => {
      return r.role.role === undefined;
    });
    const primaryRecipient = recipientWithGroup || recipients[0];

    this.props.onSelect([primaryRecipient]);
  }

  // typecheck: ignore statement
  handleDeselectGroup(e: SyntheticMouseEvent) {
    e.stopPropagation();
    const recipient = this.props.recipients[0];
    this.props.onDeselectGroup(recipient.group);
    this.setState({ open: false });
  }

  renderCheck() {
    return (
      <Coin
        onClick={(e) => this.handleDeselectGroup(e)}
        color="brand"
        size="xxxlarge">
        <Glyph name="check" color="white" size="large" solo />
      </Coin>
    );
  }

  renderRoleToken(recipient: GroupRecipient, i: number) {
    const { highlighted, onSelect, selectedRecipients, resultSelectionIndex } = this.props;

    const handleSelect = (e) => {
      e.stopPropagation();
      if (recipient.role.role === UNKNOWN) {
        this.renderUnknownRolesModal();
      } else {
        onSelect([recipient]);
      }
    };

    const handleMouseEnter = () => {
      this.props.onChangeResultSelection(i);
    };

    const selected = selectedRecipients.some(
      (s: GroupRecipient) => s.role.role === recipient.role.role
    );

    return (
      <RolesToken
        key={i}
        role={recipient.role}
        onSelect={handleSelect}
        selected={selected}
        highlighted={highlighted && i === resultSelectionIndex}
        isUnknown={recipient.role.role === UNKNOWN}
        onMouseEnter={handleMouseEnter}
      />
    );
  }

  renderUnknownRolesModal() {
    const modal = (
      <UnknownRolesModalContainer
        group={this.props.recipients[0].group}
        onClose={() => this.props.onLoadGroups(GroupStore.getGroups())}
      />
    );
    mountModal(modal, this);
  }

  render() {
    const { open } = this.state;
    const { recipients, highlighted, selectedRecipients, query } = this.props;
    const recipient = recipients[0];
    const group = recipient.group;
    const selected = !!selectedRecipients.length;

    const handleSelect = (e) => {
      e.stopPropagation();
      if (!selected) {
        this.handleSelectPrimary();
      }
    };

    return (
      <li
        onMouseEnter={this.props.onMouseEnter}
        onMouseLeave={this.props.onMouseLeave}
        onClick={handleSelect}
        className={cx('RoleBasedMessagingGroupResultItem', {
          'RoleBasedMessagingGroupResultItem-highlighted': highlighted,
          'RoleBasedMessagingGroupResultItem-selected': selected,
        })}
        key={group.id}>
        <div className="RoleBasedMessagingGroupResultItem-content">
          <div className="RoleBasedMessagingGroupResultItem-info">
            <div className="RoleBasedMessagingGroupResultItem-circle">
              {selected ?
                this.renderCheck() : <GroupAvatar group={group} size="small" />}
            </div>
            <div
              onClick={selected ? (e) => this.handleDeselectGroup(e) : handleSelect}
              className="RoleBasedMessagingGroupResultItem-name">
              <Text
                truncate
                color="pitch"
                size="small">
                <GroupName group={group} />
              </Text>
            </div>
          </div>
          <div className="RoleBasedMessagingGroupResultItem-roles-container">
            {(selected || query || open) &&
              recipients.map((r, i) => this.renderRoleToken(r, i))}
          </div>
        </div>
      </li>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    onLoadGroups: loadGroups,
    onDeselectGroup: deselectGroup,
    onChangeResultSelection: changeResultSelection,
  }, dispatch);
}

// $FlowIssue
export default connect(undefined, mapDispatchToProps)(RoleBasedMessagingGroupResultItem);



// WEBPACK FOOTER //
// ./src/chat/components/RoleBasedMessagingGroupResultItem.jsx